// src/ConveSidebar.tsx

import React, { useState, useRef, useEffect } from 'react';
import { ConveChatRoom } from './types/conve';
import { GroupRoom } from './types/groupChat';
import { conveTokenHelper } from './api/conveApi';
import ConveGroupList from './ConveGroupList';

interface Props {
  rooms: ConveChatRoom[];
  loadingRooms: boolean;
  activeRoomId: number | null;
  collapsed: boolean;
  mobileOpen: boolean;
  tokenUsage: { percent: number; used: number } | null;
  selectedModel: string;
  onToggleCollapse: () => void;
  onNewChat: () => void;
  onSelectRoom: (roomId: number) => void;
  onRenameRoom: (roomId: number, title: string) => void;
  onDeleteRoom: (roomId: number) => void;
  onSearchOpen: () => void;
  onLogout: () => void;
  theme: 'dark' | 'light';
  onToggleTheme: () => void;
  onWithdraw: () => void;
  // 그룹 채팅 관련
  groupRooms: GroupRoom[];
  activeGroupRoomId: number | null;
  onSelectGroupRoom: (roomId: number) => void;
  onNewGroupChat: () => void;
  isOnline: (userId: number) => boolean;
}

const ConveSidebar: React.FC<Props> = ({
  rooms, loadingRooms, activeRoomId, collapsed, mobileOpen,
  tokenUsage, selectedModel,
  onToggleCollapse, onNewChat, onSelectRoom,
  onRenameRoom, onDeleteRoom, onSearchOpen,
  onLogout, onWithdraw, theme, onToggleTheme,
  groupRooms, activeGroupRoomId, onSelectGroupRoom, onNewGroupChat, isOnline,
}) => {
  const [editingId, setEditingId]         = useState<number | null>(null);
  const [editTitle, setEditTitle]         = useState('');
  const [userPopup, setUserPopup]         = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [activeTab, setActiveTab]         = useState<'ai' | 'group'>('ai');
  const editRef  = useRef<HTMLInputElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const user = conveTokenHelper.getUser();
  const canSelectModel = !!(user?.email ?? '').includes('koojaeken');

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        setUserPopup(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (deleteConfirm === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Enter') { e.preventDefault(); onDeleteRoom(deleteConfirm); setDeleteConfirm(null); }
      if (e.key === 'Escape') { e.preventDefault(); setDeleteConfirm(null); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [deleteConfirm, onDeleteRoom]);

  const startEdit = (room: ConveChatRoom) => {
    setEditingId(room.id);
    setEditTitle(room.title);
    setTimeout(() => editRef.current?.focus(), 50);
  };

  const submitEdit = (roomId: number) => {
    if (editTitle.trim()) onRenameRoom(roomId, editTitle.trim());
    setEditingId(null);
  };

  // 접힌 상태 (데스크탑)
  if (collapsed) {
    return (
      <div className="conve-sidebar collapsed">
        <div className="conve-sb-head" style={{ justifyContent: 'center' }}>
          <button className="conve-icon-btn" onClick={onToggleCollapse} title="사이드바 열기">◧</button>
        </div>
        <div className="conve-sb-collapsed-actions">
          <button className="conve-collapsed-new" onClick={onNewChat} title="새 채팅">+</button>
          {rooms.slice(0, 8).map(r => (
            <div
              key={r.id}
              className={`conve-collapsed-dot ${r.id === activeRoomId ? 'active' : ''}`}
              onClick={() => onSelectRoom(r.id)}
              title={r.title}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`conve-sidebar ${mobileOpen ? 'open' : ''}`}>
      <div className="conve-sb-head">
        <div className="conve-sb-logo" onClick={() => window.location.href = '/conve'} style={{ cursor: 'pointer' }}>
          CONV<span>E</span>
        </div>
        <button className="conve-icon-btn" onClick={onToggleCollapse} title="사이드바 접기">◧</button>
      </div>

      {/* 탭 선택 */}
      <div className="conve-sb-tabs">
        <button
          className={`conve-sb-tab ${activeTab === 'ai' ? 'active' : ''}`}
          onClick={() => setActiveTab('ai')}
        >
          AI 채팅
        </button>
        <button
          className={`conve-sb-tab ${activeTab === 'group' ? 'active' : ''}`}
          onClick={() => setActiveTab('group')}
        >
          대화
          {groupRooms.reduce((sum, r) => sum + (r.unreadMessageCount ?? 0), 0) > 0 && (
            <span className="conve-tab-badge">
              {groupRooms.reduce((sum, r) => sum + (r.unreadMessageCount ?? 0), 0)}
            </span>
          )}
        </button>
      </div>

      {activeTab === 'ai' ? (
        <>
          <div className="conve-sb-actions">
            <button className="conve-new-btn" onClick={onNewChat}>
              <span className="conve-plus-circle">+</span>
              새 채팅
            </button>
            <button className="conve-search-row" onClick={onSearchOpen}>
              🔍 &nbsp;검색
            </button>
          </div>

          <div className="conve-sb-section-label">최근 항목</div>
          <div className="conve-chat-list">
            {loadingRooms ? (
              <div className="conve-rooms-loading"><span className="conve-spinner-sm" />목록 불러오는 중...</div>
            ) : rooms.length === 0 ? (
              <div className="conve-rooms-empty">대화 내역이 없습니다</div>
            ) : (
              rooms.map(room => (
                <div
                  key={room.id}
                  className={`conve-chat-item ${room.id === activeRoomId ? 'active' : ''}`}
                  onClick={() => editingId !== room.id && onSelectRoom(room.id)}
                >
                  {editingId === room.id ? (
                    <input
                      ref={editRef}
                      className="conve-edit-input"
                      value={editTitle}
                      onChange={e => setEditTitle(e.target.value)}
                      onBlur={() => submitEdit(room.id)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') submitEdit(room.id);
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                      onClick={e => e.stopPropagation()}
                    />
                  ) : (
                    <>
                      <span className="conve-chat-item-txt">{room.title}</span>
                      <div className="conve-chat-acts">
                        <button className="conve-ca" onClick={e => { e.stopPropagation(); startEdit(room); }} title="수정">✏</button>
                        <button className="conve-ca danger" onClick={e => { e.stopPropagation(); setDeleteConfirm(room.id); }} title="삭제">🗑</button>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </>
      ) : (
        <ConveGroupList
          rooms={groupRooms}
          activeRoomId={activeGroupRoomId}
          onSelectRoom={onSelectGroupRoom}
          onNewChat={onNewGroupChat}
          isOnline={isOnline}
        />
      )}

      {deleteConfirm !== null && (
        <div className="conve-delete-modal">
          <div className="conve-delete-modal-inner">
            <div className="conve-delete-msg">이 대화방을 삭제할까요?</div>
            <div className="conve-delete-btns">
              <button className="conve-delete-cancel" onClick={() => setDeleteConfirm(null)}>취소</button>
              <button className="conve-delete-confirm" onClick={() => { onDeleteRoom(deleteConfirm!); setDeleteConfirm(null); }}>삭제</button>
            </div>
          </div>
        </div>
      )}

      {/* 토큰 사용량 */}
      {tokenUsage !== null && (
        <div className="conve-token-usage">
          <div className="conve-token-row">
            {canSelectModel && (
              <span className="conve-token-model">
                {rooms.find(r => r.id === activeRoomId)?.model?.replace('claude-', '').replace(/-\d{8}$/, '') ?? selectedModel}
              </span>
            )}
            <span className={`conve-token-pct ${tokenUsage.percent >= 80 ? 'danger' : tokenUsage.percent >= 60 ? 'warn' : ''}`}>
              {tokenUsage.percent}%
            </span>
          </div>
          <div className="conve-token-bar-bg">
            <div
              className={`conve-token-bar-fill ${tokenUsage.percent >= 80 ? 'danger' : tokenUsage.percent >= 60 ? 'warn' : ''}`}
              style={{ width: `${tokenUsage.percent}%` }}
            />
          </div>
        </div>
      )}

      <div className="conve-sb-footer" ref={popupRef}>
        {userPopup && (
          <div className="conve-user-popup">
            <div className="conve-popup-email">{user?.email}</div>
            <div className="conve-popup-item" onClick={() => { onToggleTheme(); }}>{theme === 'dark' ? '☀️' : '🌙'}&nbsp; {theme === 'dark' ? '라이트 모드' : '다크 모드'}</div>
            <div className="conve-popup-divider" />
            <div className="conve-popup-item" onClick={() => { setUserPopup(false); onLogout(); }}>🚪 &nbsp;로그아웃</div>
            <div className="conve-popup-divider" />
            <div className="conve-popup-item danger" onClick={() => { setUserPopup(false); onWithdraw(); }}>💀 &nbsp;회원 탈퇴</div>
          </div>
        )}
        <div className="conve-user-row" onClick={() => setUserPopup(p => !p)}>
          <div className="conve-avatar">{user?.username ? user.username[0].toUpperCase() : 'U'}</div>
          <div className="conve-user-info">
            <div className="conve-user-name">{user?.username}</div>
            <div className="conve-user-email">{user?.email}</div>
          </div>
          <span className="conve-chevron">{userPopup ? '▼' : '▲'}</span>
        </div>
      </div>
    </div>
  );
};

export default ConveSidebar;